import { format } from "date-fns";
import { useMemo, useState } from "react";
import { ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlanReview } from "@/context/PlanReviewContext";
import { cn } from "@/lib/utils";

function statusPillClass(status: string) {
  switch (status) {
    case "complete":
      return "bg-emerald-100 text-emerald-800";
    case "failed":
      return "bg-red-100 text-red-800";
    case "processing":
    case "pending":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-slate-100 text-slate-700";
  }
}

export function PlanReviewHistoryList() {
  const { reviews, selectedReviewId, setSelectedReviewId } = usePlanReview();
  const [search, setSearch] = useState("");

  // Reviews run from a logged submission stamp the same user_id, so they
  // arrive in the same list; they belong to the Submissions tab, not here.
  const unloggedReviews = useMemo(() => reviews.filter((r) => !r.submission_id), [reviews]);

  const filteredReviews = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    if (searchLower.length === 0) return unloggedReviews;
    return unloggedReviews.filter(
      (r) =>
        r.title?.toLowerCase().includes(searchLower) ||
        format(new Date(r.created_at), "PPp").toLowerCase().includes(searchLower)
    );
  }, [unloggedReviews, search]);

  return (
    <div className="w-full max-w-[236px]">
      <div className="px-3 pt-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Unlogged reviews
        </h2>
        <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
          Your own runs. Logged submissions live in the Submissions tab.
        </p>
      </div>
      <div className="py-2 pl-3 pr-[10px]">
        <input
          type="text"
          placeholder="Search reviews..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-input bg-input px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:ring-1 focus:ring-ring transition-colors"
        />
      </div>
      <div className="py-2" role="list" aria-label="Unlogged plan reviews">
        {filteredReviews.map((review) => {
          const isActive = selectedReviewId === review.id;
          return (
            <div key={review.id} className="flex w-full items-center gap-1">
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className={cn(
                  "h-9 min-w-0 flex-1 justify-start px-3 py-0",
                  isActive && "border-2 border-primary font-medium"
                )}
                onClick={() => setSelectedReviewId(review.id)}
                role="listitem"
              >
                <ClipboardCheck className={cn("h-4 w-4 shrink-0", isActive && "text-primary")} />
                <span className="ml-2 min-w-0 overflow-hidden text-ellipsis text-left" title={review.title}>
                  {review.title || format(new Date(review.created_at), "PPp")}
                </span>
              </Button>
              <span
                className={cn(
                  "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                  statusPillClass(review.status)
                )}
                title={review.status}
              >
                {review.status === "complete" ? "✓" : review.status === "failed" ? "✗" : "…"}
              </span>
            </div>
          );
        })}
        {filteredReviews.length === 0 && (
          <div className="px-3 py-2 text-xs text-muted-foreground italic">
            {unloggedReviews.length === 0 ? "No unlogged reviews yet" : "No matching reviews"}
          </div>
        )}
      </div>
    </div>
  );
}
